/* eslint-disable @typescript-eslint/naming-convention */

import * as WebGPUConstants from "./webgpuConstants.js";
import { VertexBuffer } from "../../Buffers/buffer.js";
import { WebGPUShaderProcessor } from "./webgpuShaderProcessor.js";
import { renderableTextureFormatToIndex, WebGPUTextureHelper } from "./webgpuTextureHelper.js";
var StatePosition;
(function (StatePosition) {
    StatePosition[StatePosition["StencilReadMask"] = 0] = "StencilReadMask";
    StatePosition[StatePosition["StencilWriteMask"] = 1] = "StencilWriteMask";
    //DepthBiasClamp = 1, // not used, so remove it to improve perf
    StatePosition[StatePosition["DepthBias"] = 2] = "DepthBias";
    StatePosition[StatePosition["DepthBiasSlopeScale"] = 3] = "DepthBiasSlopeScale";
    StatePosition[StatePosition["DepthStencilState"] = 4] = "DepthStencilState";
    StatePosition[StatePosition["MRTAttachments1"] = 5] = "MRTAttachments1";
    StatePosition[StatePosition["MRTAttachments2"] = 6] = "MRTAttachments2";
    StatePosition[StatePosition["RasterizationState"] = 7] = "RasterizationState";
    StatePosition[StatePosition["ColorStates"] = 8] = "ColorStates";
    StatePosition[StatePosition["ShaderStage"] = 9] = "ShaderStage";
    StatePosition[StatePosition["TextureStage"] = 10] = "TextureStage";
    StatePosition[StatePosition["VertexState"] = 11] = "VertexState";
    StatePosition[StatePosition["NumStates"] = 12] = "NumStates";
})(StatePosition || (StatePosition = {}));
var alphaBlendFactorToIndex = {
    0: 1,
    1: 2,
    0x0300: 3,
    0x0301: 4,
    0x0302: 5,
    0x0303: 6,
    0x0304: 7,
    0x0305: 8,
    0x0306: 9,
    0x0307: 10,
    0x0308: 11,
    0x8001: 12,
    0x8002: 13,
    0x8003: 12,
    0x8004: 13, // OneMinusBlendColor (alpha)
};
var stencilOpToIndex = {
    0x0000: 0,
    0x1e00: 1,
    0x1e01: 2,
    0x1e02: 3,
    0x1e03: 4,
    0x150a: 5,
    0x8507: 6,
    0x8508: 7, // DECR_WRAP
};
/** @hidden */
var WebGPUCacheRenderPipeline = /** @class */ (function () {
    function WebGPUCacheRenderPipeline(device, emptyVertexBuffer, useTextureStage) {
        this.mrtTextureCount = 0;
        this._device = device;
        this._useTextureStage = useTextureStage;
        this._states = new Array(30); // pre-allocate enough room so that no new allocation will take place afterwards
        this._statesLength = 0;
        this._stateDirtyLowestIndex = 0;
        this._emptyVertexBuffer = emptyVertexBuffer;
        this._mrtFormats = [];
        this._parameter = { token: undefined, pipeline: null };
        this.disabled = false;
        this.vertexBuffers = [];
        this._kMaxVertexBufferStride = device.limits.maxVertexBufferArrayStride || 2048;
        this.reset();
    }
    WebGPUCacheRenderPipeline.prototype.reset = function () {
        this._isDirty = true;
        this.vertexBuffers.length = 0;
        this.setAlphaToCoverage(false);
        this.resetDepthCullingState();
        this.setClampDepth(false);
        this.setDepthBias(0);
        //this.setDepthBiasClamp(0);
        this._webgpuColorFormat = [WebGPUConstants.TextureFormat.BGRA8Unorm];
        this.setColorFormat(WebGPUConstants.TextureFormat.BGRA8Unorm);
        this.setMRT([]);
        this.setAlphaBlendEnabled(false);
        this.setAlphaBlendFactors([null, null, null, null], [null, null]);
        this.setWriteMask(0xf);
        this.setDepthStencilFormat(WebGPUConstants.TextureFormat.Depth24PlusStencil8);
        this.setStencilEnabled(false);
        this.resetStencilState();
        this.setBuffers(null, null, null);
        this._setTextureState(0);
    };
    Object.defineProperty(WebGPUCacheRenderPipeline.prototype, "colorFormats", {
        get: function () {
            return this._mrtAttachments1 > 0 ? this._mrtFormats : this._webgpuColorFormat;
        },
        enumerable: false,
        configurable: true
    });
    WebGPUCacheRenderPipeline.prototype.getRenderPipeline = function (fillMode, effect, sampleCount, textureState) {
        if (textureState === void 0) { textureState = 0; }
        if (sampleCount > 1) {
            // WebGPU only supports 1 or 4
            sampleCount = 4;
        }
        if (this.disabled) {
            var topology_1 = WebGPUCacheRenderPipeline._GetTopology(fillMode);
            this._setVertexState(effect); // to fill this.vertexBuffers with correct data
            this._parameter.pipeline = this._createRenderPipeline(effect, topology_1, sampleCount);
            WebGPUCacheRenderPipeline.NumCacheMiss++;
            WebGPUCacheRenderPipeline._NumPipelineCreationCurrentFrame++;
            return this._parameter.pipeline;
        }
        this._setShaderStage(effect.uniqueId);
        this._setRasterizationState(fillMode, sampleCount);
        this._setColorStates();
        this._setDepthStencilState();
        this._setVertexState(effect);
        this._setTextureState(textureState);
        this.lastStateDirtyLowestIndex = this._stateDirtyLowestIndex;
        if (!this._isDirty && this._parameter.pipeline) {
            this._stateDirtyLowestIndex = this._statesLength;
            WebGPUCacheRenderPipeline.NumCacheHitWithoutHash++;
            return this._parameter.pipeline;
        }
        this._getRenderPipeline(this._parameter);
        this._isDirty = false;
        this._stateDirtyLowestIndex = this._statesLength;
        if (this._parameter.pipeline) {
            WebGPUCacheRenderPipeline.NumCacheHitWithHash++;
            return this._parameter.pipeline;
        }
        var topology = WebGPUCacheRenderPipeline._GetTopology(fillMode);
        this._parameter.pipeline = this._createRenderPipeline(effect, topology, sampleCount);
        this._setRenderPipeline(this._parameter);
        WebGPUCacheRenderPipeline.NumCacheMiss++;
        WebGPUCacheRenderPipeline._NumPipelineCreationCurrentFrame++;
        return this._parameter.pipeline;
    };
    WebGPUCacheRenderPipeline.prototype.endFrame = function () {
        WebGPUCacheRenderPipeline.NumPipelineCreationLastFrame = WebGPUCacheRenderPipeline._NumPipelineCreationCurrentFrame;
        WebGPUCacheRenderPipeline._NumPipelineCreationCurrentFrame = 0;
    };
    WebGPUCacheRenderPipeline.prototype.setAlphaToCoverage = function (enabled) {
        this._alphaToCoverageEnabled = enabled;
    };
    WebGPUCacheRenderPipeline.prototype.setFrontFace = function (frontFace) {
        this._frontFace = frontFace;
    };
    WebGPUCacheRenderPipeline.prototype.setCullEnabled = function (enabled) {
        this._cullEnabled = enabled;
    };
    WebGPUCacheRenderPipeline.prototype.setCullFace = function (cullFace) {
        this._cullFace = cullFace;
    };
    WebGPUCacheRenderPipeline.prototype.setClampDepth = function (clampDepth) {
        this._clampDepth = clampDepth;
    };
    WebGPUCacheRenderPipeline.prototype.resetDepthCullingState = function () {
        this.setDepthCullingState(false, 2, 1, 0, 0, true, true, 519);
    };
    WebGPUCacheRenderPipeline.prototype.setDepthCullingState = function (cullEnabled, frontFace, cullFace, zOffset, zOffsetUnits, depthTestEnabled, depthWriteEnabled, depthCompare) {
        this._depthWriteEnabled = depthWriteEnabled;
        this._depthTestEnabled = depthTestEnabled;
        this._depthCompare = (depthCompare !== null && depthCompare !== void 0 ? depthCompare : 519) - 0x0200;
        this._cullFace = cullFace;
        this._cullEnabled = cullEnabled;
        this._frontFace = frontFace;
        this.setDepthBiasSlopeScale(zOffset);
        this.setDepthBias(zOffsetUnits);
    };
    WebGPUCacheRenderPipeline.prototype.setDepthBias = function (depthBias) {
        if (this._depthBias !== depthBias) {
            this._depthBias = depthBias;
            this._states[StatePosition.DepthBias] = depthBias;
            this._isDirty = true;
            this._stateDirtyLowestIndex = Math.min(this._stateDirtyLowestIndex, StatePosition.DepthBias);
        }
    };
    /*public setDepthBiasClamp(depthBiasClamp: number): void {
        if (this._depthBiasClamp !== depthBiasClamp) {
            this._depthBiasClamp = depthBiasClamp;
            this._states[StatePosition.DepthBiasClamp] = depthBiasClamp.toString();
            this._isDirty = true;
        }
    }*/
    WebGPUCacheRenderPipeline.prototype.setDepthBiasSlopeScale = function (depthBiasSlopeScale) {
        if (this._depthBiasSlopeScale !== depthBiasSlopeScale) {
            this._depthBiasSlopeScale = depthBiasSlopeScale;
            this._states[StatePosition.DepthBiasSlopeScale] = depthBiasSlopeScale;
            this._isDirty = true;
            this._stateDirtyLowestIndex = Math.min(this._stateDirtyLowestIndex, StatePosition.DepthBiasSlopeScale);
        }
    };
    WebGPUCacheRenderPipeline.prototype.setColorFormat = function (format) {
        this._webgpuColorFormat[0] = format;
        this._colorFormat = renderableTextureFormatToIndex[format !== null && format !== void 0 ? format : ""];
    };
    WebGPUCacheRenderPipeline.prototype.setMRTAttachments = function (attachments) {
        this.mrtAttachments = attachments;
        var mask = 0;
        for (var i = 0; i < attachments.length; ++i) {
            if (attachments[i] !== 0) {
                mask += 1 << i;
            }
        }
        if (this._mrtEnabledMask !== mask) {
            this._mrtEnabledMask = mask;
            this._isDirty = true;
            this._stateDirtyLowestIndex = Math.min(this._stateDirtyLowestIndex, StatePosition.MRTAttachments1);
        }
    };
    WebGPUCacheRenderPipeline.prototype.setMRT = function (textureArray, textureCount) {
        var _a, _b;
        textureCount = textureCount !== null && textureCount !== void 0 ? textureCount : textureArray.length;
        if (textureCount > 10) {
            // If we want more than 10 attachments we need to change this method (and the StatePosition enum) but 10 seems plenty: note that WebGPU only supports 8 at the time (2021/12/13)!
            // As we need 39 different values we are using 6 bits to encode a texture format, meaning we can encode 5 texture formats in 32 bits
            // We are using 2x32 bit values to handle 10 textures
            throw "Can't handle more than 10 attachments for a MRT in cache render pipeline!";
        }
        this.mrtTextureArray = textureArray;
        this.mrtTextureCount = textureCount;
        this._mrtEnabledMask = 0xffff; // all textures are enabled at start (meaning we can write to them). Calls to setMRTAttachments may disable some
        var bits = [0, 0];
        var indexBits = 0, mask = 0, numRT = 0;
        for (var i = 0; i < textureCount; ++i) {
            var texture = textureArray[i];
            var gpuWrapper = texture === null || texture === void 0 ? void 0 : texture._hardwareTexture;
            this._mrtFormats[numRT] = (_a = gpuWrapper === null || gpuWrapper === void 0 ? void 0 : gpuWrapper.format) !== null && _a !== void 0 ? _a : this._webgpuColorFormat[0];
            bits[indexBits] += renderableTextureFormatToIndex[(_b = this._mrtFormats[numRT]) !== null && _b !== void 0 ? _b : ""] << mask;
            mask += 6;
            numRT++;
            if (mask >= 32) {
                mask = 0;
                indexBits++;
            }
        }
        this._mrtFormats.length = numRT;
        if (this._mrtAttachments1 !== bits[0] || this._mrtAttachments2 !== bits[1]) {
            this._mrtAttachments1 = bits[0];
            this._mrtAttachments2 = bits[1];
            this._states[StatePosition.MRTAttachments1] = bits[0];
            this._states[StatePosition.MRTAttachments2] = bits[1];
            this._isDirty = true;
            this._stateDirtyLowestIndex = Math.min(this._stateDirtyLowestIndex, StatePosition.MRTAttachments1);
        }
    };
    WebGPUCacheRenderPipeline.prototype.setAlphaBlendEnabled = function (enabled) {
        this._alphaBlendEnabled = enabled;
    };
    WebGPUCacheRenderPipeline.prototype.setAlphaBlendFactors = function (factors, operations) {
        this._alphaBlendFuncParams = factors;
        this._alphaBlendEqParams = operations;
    };
    WebGPUCacheRenderPipeline.prototype.setWriteMask = function (mask) {
        this._writeMask = mask;
    };
    WebGPUCacheRenderPipeline.prototype.setDepthStencilFormat = function (format) {
        this._webgpuDepthStencilFormat = format;
        this._depthStencilFormat = format === undefined ? 0 : renderableTextureFormatToIndex[format];
    };
    WebGPUCacheRenderPipeline.prototype.setDepthTestEnabled = function (enabled) {
        this._depthTestEnabled = enabled;
    };
    WebGPUCacheRenderPipeline.prototype.setDepthWriteEnabled = function (enabled) {
        this._depthWriteEnabled = enabled;
    };
    WebGPUCacheRenderPipeline.prototype.setDepthCompare = function (func) {
        this._depthCompare = (func !== null && func !== void 0 ? func : 519) - 0x0200;
    };
    WebGPUCacheRenderPipeline.prototype.setStencilEnabled = function (enabled) {
        this._stencilEnabled = enabled;
    };
    WebGPUCacheRenderPipeline.prototype.setStencilCompare = function (func) {
        this._stencilFrontCompare = (func !== null && func !== void 0 ? func : 519) - 0x0200;
    };
    WebGPUCacheRenderPipeline.prototype.setStencilDepthFailOp = function (op) {
        this._stencilFrontDepthFailOp = op === null ? 1 /* KEEP */ : stencilOpToIndex[op];
    };
    WebGPUCacheRenderPipeline.prototype.setStencilPassOp = function (op) {
        this._stencilFrontPassOp = op === null ? 2 /* REPLACE */ : stencilOpToIndex[op];
    };
    WebGPUCacheRenderPipeline.prototype.setStencilFailOp = function (op) {
        this._stencilFrontFailOp = op === null ? 1 /* KEEP */ : stencilOpToIndex[op];
    };
    WebGPUCacheRenderPipeline.prototype.setStencilReadMask = function (mask) {
        if (this._stencilReadMask !== mask) {
            this._stencilReadMask = mask;
            this._states[StatePosition.StencilReadMask] = mask;
            this._isDirty = true;
            this._stateDirtyLowestIndex = Math.min(this._stateDirtyLowestIndex, StatePosition.StencilReadMask);
        }
    };
    WebGPUCacheRenderPipeline.prototype.setStencilWriteMask = function (mask) {
        if (this._stencilWriteMask !== mask) {
            this._stencilWriteMask = mask;
            this._states[StatePosition.StencilWriteMask] = mask;
            this._isDirty = true;
            this._stateDirtyLowestIndex = Math.min(this._stateDirtyLowestIndex, StatePosition.StencilWriteMask);
        }
    };
    WebGPUCacheRenderPipeline.prototype.resetStencilState = function () {
        this.setStencilState(false, 519, 7680, 7681, 7680, 0xff, 0xff);
    };
    WebGPUCacheRenderPipeline.prototype.setStencilState = function (stencilEnabled, compare, depthFailOp, passOp, failOp, readMask, writeMask) {
        this._stencilEnabled = stencilEnabled;
        this._stencilFrontCompare = (compare !== null && compare !== void 0 ? compare : 519) - 0x0200;
        this._stencilFrontDepthFailOp = depthFailOp === null ? 1 /* KEEP */ : stencilOpToIndex[depthFailOp];
        this._stencilFrontPassOp = passOp === null ? 2 /* REPLACE */ : stencilOpToIndex[passOp];
        this._stencilFrontFailOp = failOp === null ? 1 /* KEEP */ : stencilOpToIndex[failOp];
        this.setStencilReadMask(readMask);
        this.setStencilWriteMask(writeMask);
    };
    WebGPUCacheRenderPipeline.prototype.setBuffers = function (vertexBuffers, indexBuffer, overrideVertexBuffers) {
        this._vertexBuffers = vertexBuffers;
        this._overrideVertexBuffers = overrideVertexBuffers;
        this._indexBuffer = indexBuffer;
    };
    WebGPUCacheRenderPipeline._GetTopology = function (fillMode) {
        switch (fillMode) {
            // Triangle views
            case 0:
                return WebGPUConstants.PrimitiveTopology.TriangleList;
            case 2:
                return WebGPUConstants.PrimitiveTopology.PointList;
            case 1:
                return WebGPUConstants.PrimitiveTopology.LineList;
            // Draw modes
            case 3:
                return WebGPUConstants.PrimitiveTopology.PointList;
            case 4:
                return WebGPUConstants.PrimitiveTopology.LineList;
            case 5:
                // return this._gl.LINE_LOOP;
                // TODO WEBGPU. Line Loop Mode Fallback at buffer load time.
                throw "LineLoop is an unsupported fillmode in WebGPU";
            case 6:
                return WebGPUConstants.PrimitiveTopology.LineStrip;
            case 7:
                return WebGPUConstants.PrimitiveTopology.TriangleStrip;
            case 8:
                // return this._gl.TRIANGLE_FAN;
                // TODO WEBGPU. Triangle Fan Mode Fallback at buffer load time.
                throw "TriangleFan is an unsupported fillmode in WebGPU";
            default:
                return WebGPUConstants.PrimitiveTopology.TriangleList;
        }
    };
    WebGPUCacheRenderPipeline._GetAphaBlendOperation = function (operation) {
        switch (operation) {
            case 32774:
                return WebGPUConstants.BlendOperation.Add;
            case 32778:
                return WebGPUConstants.BlendOperation.Subtract;
            case 32779:
                return WebGPUConstants.BlendOperation.ReverseSubtract;
            case 32775:
                return WebGPUConstants.BlendOperation.Min;
            case 32776:
                return WebGPUConstants.BlendOperation.Max;
            default:
                return WebGPUConstants.BlendOperation.Add;
        }
    };
    WebGPUCacheRenderPipeline._GetAphaBlendFactor = function (factor) {
        switch (factor) {
            case 0:
                return WebGPUConstants.BlendFactor.Zero;
            case 1:
                return WebGPUConstants.BlendFactor.One;
            case 768:
                return WebGPUConstants.BlendFactor.Src;
            case 769:
                return WebGPUConstants.BlendFactor.OneMinusSrc;
            case 770:
                return WebGPUConstants.BlendFactor.SrcAlpha;
            case 771:
                return WebGPUConstants.BlendFactor.OneMinusSrcAlpha;
            case 772:
                return WebGPUConstants.BlendFactor.DstAlpha;
            case 773:
                return WebGPUConstants.BlendFactor.OneMinusDstAlpha;
            case 774:
                return WebGPUConstants.BlendFactor.Dst;
            case 775:
                return WebGPUConstants.BlendFactor.OneMinusDst;
            case 776:
                return WebGPUConstants.BlendFactor.SrcAlphaSaturated;
            case 32769:
                return WebGPUConstants.BlendFactor.Constant;
            case 32770:
                return WebGPUConstants.BlendFactor.OneMinusConstant;
            case 32771:
                return WebGPUConstants.BlendFactor.Constant;
            case 32772:
                return WebGPUConstants.BlendFactor.OneMinusConstant;
            default:
                return WebGPUConstants.BlendFactor.One;
        }
    };
    WebGPUCacheRenderPipeline._GetCompareFunction = function (compareFunction) {
        switch (compareFunction) {
            case 0: // NEVER
                return WebGPUConstants.CompareFunction.Never;
            case 1: // LESS
                return WebGPUConstants.CompareFunction.Less;
            case 2: // EQUAL
                return WebGPUConstants.CompareFunction.Equal;
            case 3: // LEQUAL
                return WebGPUConstants.CompareFunction.LessEqual;
            case 4: // GREATER
                return WebGPUConstants.CompareFunction.Greater;
            case 5: // NOTEQUAL
                return WebGPUConstants.CompareFunction.NotEqual;
            case 6: // GEQUAL
                return WebGPUConstants.CompareFunction.GreaterEqual;
            case 7: // ALWAYS
                return WebGPUConstants.CompareFunction.Always;
        }
        return WebGPUConstants.CompareFunction.Never;
    };
    WebGPUCacheRenderPipeline._GetStencilOpFunction = function (operation) {
        switch (operation) {
            case 0:
                return WebGPUConstants.StencilOperation.Zero;
            case 1:
                return WebGPUConstants.StencilOperation.Keep;
            case 2:
                return WebGPUConstants.StencilOperation.Replace;
            case 3:
                return WebGPUConstants.StencilOperation.IncrementClamp;
            case 4:
                return WebGPUConstants.StencilOperation.DecrementClamp;
            case 5:
                return WebGPUConstants.StencilOperation.Invert;
            case 6:
                return WebGPUConstants.StencilOperation.IncrementWrap;
            case 7:
                return WebGPUConstants.StencilOperation.DecrementWrap;
        }
        return WebGPUConstants.StencilOperation.Keep;
    };
    WebGPUCacheRenderPipeline._GetVertexInputDescriptorFormat = function (vertexBuffer) {
        var type = vertexBuffer.type;
        var normalized = vertexBuffer.normalized;
        var size = vertexBuffer.getSize();
        switch (type) {
            case VertexBuffer.BYTE:
                switch (size) {
                    case 1:
                    case 2:
                        return normalized ? WebGPUConstants.VertexFormat.Snorm8x2 : WebGPUConstants.VertexFormat.Sint8x2;
                    case 3:
                    case 4:
                        return normalized ? WebGPUConstants.VertexFormat.Snorm8x4 : WebGPUConstants.VertexFormat.Sint8x4;
                }
                break;
            case VertexBuffer.UNSIGNED_BYTE:
                switch (size) {
                    case 1:
                    case 2:
                        return normalized ? WebGPUConstants.VertexFormat.Unorm8x2 : WebGPUConstants.VertexFormat.Uint8x2;
                    case 3:
                    case 4:
                        return normalized ? WebGPUConstants.VertexFormat.Unorm8x4 : WebGPUConstants.VertexFormat.Uint8x4;
                }
                break;
            case VertexBuffer.SHORT:
                switch (size) {
                    case 1:
                    case 2:
                        return normalized ? WebGPUConstants.VertexFormat.Snorm16x2 : WebGPUConstants.VertexFormat.Sint16x2;
                    case 3:
                    case 4:
                        return normalized ? WebGPUConstants.VertexFormat.Snorm16x4 : WebGPUConstants.VertexFormat.Sint16x4;
                }
                break;
            case VertexBuffer.UNSIGNED_SHORT:
                switch (size) {
                    case 1:
                    case 2:
                        return normalized ? WebGPUConstants.VertexFormat.Unorm16x2 : WebGPUConstants.VertexFormat.Uint16x2;
                    case 3:
                    case 4:
                        return normalized ? WebGPUConstants.VertexFormat.Unorm16x4 : WebGPUConstants.VertexFormat.Uint16x4;
                }
                break;
            case VertexBuffer.INT:
                switch (size) {
                    case 1:
                        return WebGPUConstants.VertexFormat.Sint32;
                    case 2:
                        return WebGPUConstants.VertexFormat.Sint32x2;
                    case 3:
                        return WebGPUConstants.VertexFormat.Sint32x3;
                    case 4:
                        return WebGPUConstants.VertexFormat.Sint32x4;
                }
                break;
            case VertexBuffer.UNSIGNED_INT:
                switch (size) {
                    case 1:
                        return WebGPUConstants.VertexFormat.Uint32;
                    case 2:
                        return WebGPUConstants.VertexFormat.Uint32x2;
                    case 3:
                        return WebGPUConstants.VertexFormat.Uint32x3;
                    case 4:
                        return WebGPUConstants.VertexFormat.Uint32x4;
                }
                break;
            case VertexBuffer.FLOAT:
                switch (size) {
                    case 1:
                        return WebGPUConstants.VertexFormat.Float32;
                    case 2:
                        return WebGPUConstants.VertexFormat.Float32x2;
                    case 3:
                        return WebGPUConstants.VertexFormat.Float32x3;
                    case 4:
                        return WebGPUConstants.VertexFormat.Float32x4;
                }
                break;
        }
        throw new Error("Invalid Format '".concat(vertexBuffer.getKind(), "' - type=").concat(type, ", normalized=").concat(normalized, ", size=").concat(size));
    };
    WebGPUCacheRenderPipeline.prototype._getAphaBlendState = function () {
        if (!this._alphaBlendEnabled) {
            return null;
        }
        return {
            srcFactor: WebGPUCacheRenderPipeline._GetAphaBlendFactor(this._alphaBlendFuncParams[2]),
            dstFactor: WebGPUCacheRenderPipeline._GetAphaBlendFactor(this._alphaBlendFuncParams[3]),
            operation: WebGPUCacheRenderPipeline._GetAphaBlendOperation(this._alphaBlendEqParams[1]),
        };
    };
    WebGPUCacheRenderPipeline.prototype._getColorBlendState = function () {
        if (!this._alphaBlendEnabled) {
            return null;
        }
        return {
            srcFactor: WebGPUCacheRenderPipeline._GetAphaBlendFactor(this._alphaBlendFuncParams[0]),
            dstFactor: WebGPUCacheRenderPipeline._GetAphaBlendFactor(this._alphaBlendFuncParams[1]),
            operation: WebGPUCacheRenderPipeline._GetAphaBlendOperation(this._alphaBlendEqParams[0]),
        };
    };
    WebGPUCacheRenderPipeline.prototype._setShaderStage = function (id) {
        if (this._shaderId !== id) {
            this._shaderId = id;
            this._states[StatePosition.ShaderStage] = id;
            this._isDirty = true;
            this._stateDirtyLowestIndex = Math.min(this._stateDirtyLowestIndex, StatePosition.ShaderStage);
        }
    };
    WebGPUCacheRenderPipeline.prototype._setRasterizationState = function (topology, sampleCount) {
        var frontFace = this._frontFace;
        var cullMode = this._cullEnabled ? this._cullFace : 0;
        var clampDepth = this._clampDepth ? 1 : 0;
        var alphaToCoverage = this._alphaToCoverageEnabled ? 1 : 0;
        var rasterizationState = frontFace - 1 + (cullMode << 1) + (clampDepth << 3) + (alphaToCoverage << 4) + (topology << 5) + (sampleCount << 8);
        if (this._rasterizationState !== rasterizationState) {
            this._rasterizationState = rasterizationState;
            this._states[StatePosition.RasterizationState] = this._rasterizationState;
            this._isDirty = true;
            this._stateDirtyLowestIndex = Math.min(this._stateDirtyLowestIndex, StatePosition.RasterizationState);
        }
    };
    WebGPUCacheRenderPipeline.prototype._setColorStates = function () {
        var colorStates = ((this._writeMask ? 1 : 0) << 22) + (this._colorFormat << 23) + ((this._depthWriteEnabled ? 1 : 0) << 29); // this state has been moved from depthStencilState here because alpha and depth are related (generally when alpha is on, depth write is off and the other way around)
        if (this._alphaBlendEnabled) {
            colorStates +=
                ((this._alphaBlendFuncParams[0] === null ? 2 : alphaBlendFactorToIndex[this._alphaBlendFuncParams[0]]) << 0) +
                    ((this._alphaBlendFuncParams[1] === null ? 2 : alphaBlendFactorToIndex[this._alphaBlendFuncParams[1]]) << 4) +
                    ((this._alphaBlendFuncParams[2] === null ? 2 : alphaBlendFactorToIndex[this._alphaBlendFuncParams[2]]) << 8) +
                    ((this._alphaBlendFuncParams[3] === null ? 2 : alphaBlendFactorToIndex[this._alphaBlendFuncParams[3]]) << 12) +
                    ((this._alphaBlendEqParams[0] === null ? 1 : this._alphaBlendEqParams[0] - 0x8005) << 16) +
                    ((this._alphaBlendEqParams[1] === null ? 1 : this._alphaBlendEqParams[1] - 0x8005) << 19);
        }
        if (colorStates !== this._colorStates) {
            this._colorStates = colorStates;
            this._states[StatePosition.ColorStates] = this._colorStates;
            this._isDirty = true;
            this._stateDirtyLowestIndex = Math.min(this._stateDirtyLowestIndex, StatePosition.ColorStates);
        }
    };
    WebGPUCacheRenderPipeline.prototype._setDepthStencilState = function () {
        var stencilState = !this._stencilEnabled
            ? 7 /* ALWAYS */ + (1 /* KEEP */ << 3) + (1 /* KEEP */ << 6) + (1 /* KEEP */ << 9)
            : this._stencilFrontCompare + (this._stencilFrontDepthFailOp << 3) + (this._stencilFrontPassOp << 6) + (this._stencilFrontFailOp << 9);
        var depthStencilState = this._depthStencilFormat + ((this._depthTestEnabled ? this._depthCompare : 7) /* ALWAYS */ << 6) + (stencilState << 10); // stencil front - stencil back is the same
        if (this._depthStencilState !== depthStencilState) {
            this._depthStencilState = depthStencilState;
            this._states[StatePosition.DepthStencilState] = this._depthStencilState;
            this._isDirty = true;
            this._stateDirtyLowestIndex = Math.min(this._stateDirtyLowestIndex, StatePosition.DepthStencilState);
        }
    };
    WebGPUCacheRenderPipeline.prototype._setVertexState = function (effect) {
        var _a, _b;
        var currStateLen = this._statesLength;
        var newNumStates = StatePosition.VertexState;
        var webgpuPipelineContext = effect._pipelineContext;
        var attributes = webgpuPipelineContext.shaderProcessingContext.attributeNamesFromEffect;
        var locations = webgpuPipelineContext.shaderProcessingContext.attributeLocationsFromEffect;
        var currentGPUBuffer;
        var numVertexBuffers = 0;
        for (var index = 0; index < attributes.length; index++) {
            var location_1 = locations[index];
            var vertexBuffer = (_a = (this._overrideVertexBuffers && this._overrideVertexBuffers[attributes[index]])) !== null && _a !== void 0 ? _a : this._vertexBuffers[attributes[index]];
            if (!vertexBuffer) {
                // In WebGL it's valid to not bind a vertex buffer to an attribute, but it's not valid in WebGPU
                // So we must bind a dummy buffer when we are not given one for a specific attribute
                vertexBuffer = this._emptyVertexBuffer;
            }
            var buffer = (_b = vertexBuffer.getBuffer()) === null || _b === void 0 ? void 0 : _b.underlyingResource;
            // We optimize usage of GPUVertexBufferLayout: we will create a single GPUVertexBufferLayout for all the attributes which follow each other and which use the same GPU buffer
            // However, there are some constraints in the attribute.offset value range, so we must check for them before being able to reuse the same GPUVertexBufferLayout
            // See _getVertexInputDescriptor() below
            if (vertexBuffer._validOffsetRange === undefined) {
                var offset = vertexBuffer.byteOffset;
                var formatSize = vertexBuffer.getSize(true);
                var byteStride = vertexBuffer.byteStride;
                vertexBuffer._validOffsetRange = offset <= this._kMaxVertexBufferStride - formatSize && (byteStride === 0 || offset + formatSize <= byteStride);
            }
            if (!(currentGPUBuffer && currentGPUBuffer === buffer && vertexBuffer._validOffsetRange)) {
                // we can't combine the previous vertexBuffer with the current one
                this.vertexBuffers[numVertexBuffers++] = vertexBuffer;
                currentGPUBuffer = vertexBuffer._validOffsetRange ? buffer : null;
            }
            var vid = vertexBuffer.hashCode + (location_1 << 7);
            this._isDirty = this._isDirty || this._states[newNumStates] !== vid;
            this._states[newNumStates++] = vid;
        }
        this.vertexBuffers.length = numVertexBuffers;
        this._statesLength = newNumStates;
        this._isDirty = this._isDirty || newNumStates !== currStateLen;
        if (this._isDirty) {
            this._stateDirtyLowestIndex = Math.min(this._stateDirtyLowestIndex, StatePosition.VertexState);
        }
    };
    WebGPUCacheRenderPipeline.prototype._setTextureState = function (textureState) {
        if (this._textureState !== textureState) {
            this._textureState = textureState;
            this._states[StatePosition.TextureStage] = this._textureState;
            this._isDirty = true;
            this._stateDirtyLowestIndex = Math.min(this._stateDirtyLowestIndex, StatePosition.TextureStage);
        }
    };
    WebGPUCacheRenderPipeline.prototype._createPipelineLayout = function (webgpuPipelineContext) {
        if (this._useTextureStage) {
            return this._createPipelineLayoutWithTextureStage(webgpuPipelineContext);
        }
        var bindGroupLayouts = [];
        var bindGroupLayoutEntries = webgpuPipelineContext.shaderProcessingContext.bindGroupLayoutEntries;
        for (var i = 0; i < bindGroupLayoutEntries.length; i++) {
            var setDefinition = bindGroupLayoutEntries[i];
            bindGroupLayouts[i] = this._device.createBindGroupLayout({
                entries: setDefinition,
            });
        }
        webgpuPipelineContext.bindGroupLayouts = bindGroupLayouts;
        return this._device.createPipelineLayout({ bindGroupLayouts: bindGroupLayouts });
    };
    WebGPUCacheRenderPipeline.prototype._createPipelineLayoutWithTextureStage = function (webgpuPipelineContext) {
        var _a;
        var shaderProcessingContext = webgpuPipelineContext.shaderProcessingContext;
        var bindGroupLayoutEntries = shaderProcessingContext.bindGroupLayoutEntries;
        var bitVal = 1;
        for (var i = 0; i < bindGroupLayoutEntries.length; i++) {
            var setDefinition = bindGroupLayoutEntries[i];
            for (var j = 0; j < setDefinition.length; j++) {
                var entry = bindGroupLayoutEntries[i][j];
                if (entry.texture) {
                    var name_1 = shaderProcessingContext.bindGroupLayoutEntryInfo[i][entry.binding].name;
                    var textureInfo = shaderProcessingContext.availableTextures[name_1];
                    var samplerInfo = textureInfo.autoBindSampler ? shaderProcessingContext.availableSamplers[name_1 + WebGPUShaderProcessor.AutoSamplerSuffix] : null;
                    var sampleType = textureInfo.sampleType;
                    var samplerType = (_a = samplerInfo === null || samplerInfo === void 0 ? void 0 : samplerInfo.type) !== null && _a !== void 0 ? _a : WebGPUConstants.SamplerBindingType.Filtering;
                    if (this._textureState & bitVal && sampleType !== WebGPUConstants.TextureSampleType.Depth) {
                        // The texture is a 32 bits float texture but the system does not support linear filtering for them:
                        // we set the sampler to "non-filtering" and the texture sample type to "unfilterable-float"
                        if (textureInfo.autoBindSampler) {
                            samplerType = WebGPUConstants.SamplerBindingType.NonFiltering;
                        }
                        sampleType = WebGPUConstants.TextureSampleType.UnfilterableFloat;
                    }
                    entry.texture.sampleType = sampleType;
                    if (samplerInfo) {
                        var binding = shaderProcessingContext.bindGroupLayoutEntryInfo[samplerInfo.binding.groupIndex][samplerInfo.binding.bindingIndex].index;
                        bindGroupLayoutEntries[samplerInfo.binding.groupIndex][binding].sampler.type = samplerType;
                    }
                    bitVal = bitVal << 1;
                }
            }
        }
        var bindGroupLayouts = [];
        for (var i = 0; i < bindGroupLayoutEntries.length; ++i) {
            bindGroupLayouts[i] = this._device.createBindGroupLayout({
                entries: bindGroupLayoutEntries[i],
            });
        }
        webgpuPipelineContext.bindGroupLayouts = bindGroupLayouts;
        return this._device.createPipelineLayout({ bindGroupLayouts: bindGroupLayouts });
    };
    WebGPUCacheRenderPipeline.prototype._getVertexInputDescriptor = function (effect) {
        var _a, _b;
        var descriptors = [];
        var webgpuPipelineContext = effect._pipelineContext;
        var attributes = webgpuPipelineContext.shaderProcessingContext.attributeNamesFromEffect;
        var locations = webgpuPipelineContext.shaderProcessingContext.attributeLocationsFromEffect;
        var currentGPUBuffer;
        var currentGPUAttributes;
        for (var index = 0; index < attributes.length; index++) {
            var location_2 = locations[index];
            var vertexBuffer = (_a = (this._overrideVertexBuffers && this._overrideVertexBuffers[attributes[index]])) !== null && _a !== void 0 ? _a : this._vertexBuffers[attributes[index]];
            if (!vertexBuffer) {
                // In WebGL it's valid to not bind a vertex buffer to an attribute, but it's not valid in WebGPU
                // So we must bind a dummy buffer when we are not given one for a specific attribute
                vertexBuffer = this._emptyVertexBuffer;
            }
            var buffer = (_b = vertexBuffer.getBuffer()) === null || _b === void 0 ? void 0 : _b.underlyingResource;
            // We reuse the same GPUVertexBufferLayout for all attributes that use the same underlying GPU buffer (and for attributes that follow each other in the attributes array)
            var offset = vertexBuffer.byteOffset;
            var invalidOffsetRange = !vertexBuffer._validOffsetRange;
            if (!(currentGPUBuffer && currentGPUAttributes && currentGPUBuffer === buffer) || invalidOffsetRange) {
                var vertexBufferDescriptor = {
                    arrayStride: vertexBuffer.byteStride,
                    stepMode: vertexBuffer.getIsInstanced() ? WebGPUConstants.InputStepMode.Instance : WebGPUConstants.InputStepMode.Vertex,
                    attributes: [],
                };
                descriptors.push(vertexBufferDescriptor);
                currentGPUAttributes = vertexBufferDescriptor.attributes;
                if (invalidOffsetRange) {
                    offset = 0; // the offset will be set directly in the setVertexBuffer call
                    buffer = null; // buffer can't be reused
                }
            }
            currentGPUAttributes.push({
                shaderLocation: location_2,
                offset: offset,
                format: WebGPUCacheRenderPipeline._GetVertexInputDescriptorFormat(vertexBuffer),
            });
            currentGPUBuffer = buffer;
        }
        return descriptors;
    };
    WebGPUCacheRenderPipeline.prototype._createRenderPipeline = function (effect, topology, sampleCount) {
        var webgpuPipelineContext = effect._pipelineContext;
        var inputStateDescriptor = this._getVertexInputDescriptor(effect);
        var pipelineLayout = this._createPipelineLayout(webgpuPipelineContext);
        var colorStates = [];
        var alphaBlend = this._getAphaBlendState();
        var colorBlend = this._getColorBlendState();
        if (this._mrtAttachments1 > 0) {
            for (var i = 0; i < this._mrtFormats.length; ++i) {
                var format = this._mrtFormats[i];
                if (format) {
                    var descr = {
                        format: format,
                        writeMask: (this._mrtEnabledMask & (1 << i)) !== 0 ? this._writeMask : 0,
                    };
                    if (alphaBlend && colorBlend) {
                        descr.blend = {
                            alpha: alphaBlend,
                            color: colorBlend,
                        };
                    }
                    colorStates.push(descr);
                }
                else {
                    colorStates.push(null);
                }
            }
        }
        else {
            if (this._webgpuColorFormat[0]) {
                var descr = {
                    format: this._webgpuColorFormat[0],
                    writeMask: this._writeMask,
                };
                if (alphaBlend && colorBlend) {
                    descr.blend = {
                        alpha: alphaBlend,
                        color: colorBlend,
                    };
                }
                colorStates.push(descr);
            }
            else {
                colorStates.push(null);
            }
        }
        var stencilFrontBack = {
            compare: WebGPUCacheRenderPipeline._GetCompareFunction(this._stencilEnabled ? this._stencilFrontCompare : 7 /* ALWAYS */),
            depthFailOp: WebGPUCacheRenderPipeline._GetStencilOpFunction(this._stencilEnabled ? this._stencilFrontDepthFailOp : 1 /* KEEP */),
            failOp: WebGPUCacheRenderPipeline._GetStencilOpFunction(this._stencilEnabled ? this._stencilFrontFailOp : 1 /* KEEP */),
            passOp: WebGPUCacheRenderPipeline._GetStencilOpFunction(this._stencilEnabled ? this._stencilFrontPassOp : 1 /* KEEP */),
        };
        var stripIndexFormat = undefined;
        if (topology === WebGPUConstants.PrimitiveTopology.LineStrip || topology === WebGPUConstants.PrimitiveTopology.TriangleStrip) {
            stripIndexFormat = !this._indexBuffer || this._indexBuffer.is32Bits ? WebGPUConstants.IndexFormat.Uint32 : WebGPUConstants.IndexFormat.Uint16;
        }
        var depthStencilFormatHasStencil = this._webgpuDepthStencilFormat ? WebGPUTextureHelper.HasStencilAspect(this._webgpuDepthStencilFormat) : false;
        return this._device.createRenderPipeline({
            layout: pipelineLayout,
            vertex: {
                module: webgpuPipelineContext.stages.vertexStage.module,
                entryPoint: webgpuPipelineContext.stages.vertexStage.entryPoint,
                buffers: inputStateDescriptor,
            },
            primitive: {
                topology: topology,
                stripIndexFormat: stripIndexFormat,
                frontFace: this._frontFace === 1 ? WebGPUConstants.FrontFace.CCW : WebGPUConstants.FrontFace.CW,
                cullMode: !this._cullEnabled ? WebGPUConstants.CullMode.None : this._cullFace === 2 ? WebGPUConstants.CullMode.Front : WebGPUConstants.CullMode.Back,
            },
            fragment: !webgpuPipelineContext.stages.fragmentStage
                ? undefined
                : {
                    module: webgpuPipelineContext.stages.fragmentStage.module,
                    entryPoint: webgpuPipelineContext.stages.fragmentStage.entryPoint,
                    targets: colorStates,
                },
            multisample: {
                count: sampleCount,
                /*mask,
                alphaToCoverageEnabled,*/
            },
            depthStencil: this._webgpuDepthStencilFormat === undefined
                ? undefined
                : {
                    depthWriteEnabled: this._depthWriteEnabled,
                    depthCompare: this._depthTestEnabled ? WebGPUCacheRenderPipeline._GetCompareFunction(this._depthCompare) : WebGPUConstants.CompareFunction.Always,
                    format: this._webgpuDepthStencilFormat,
                    stencilFront: this._stencilEnabled && depthStencilFormatHasStencil ? stencilFrontBack : undefined,
                    stencilBack: this._stencilEnabled && depthStencilFormatHasStencil ? stencilFrontBack : undefined,
                    stencilReadMask: this._stencilEnabled && depthStencilFormatHasStencil ? this._stencilReadMask : undefined,
                    stencilWriteMask: this._stencilEnabled && depthStencilFormatHasStencil ? this._stencilWriteMask : undefined,
                    depthBias: this._depthBias,
                    depthBiasClamp: this._depthBiasClamp,
                    depthBiasSlopeScale: this._depthBiasSlopeScale,
                },
        });
    };
    WebGPUCacheRenderPipeline.NumCacheHitWithoutHash = 0;
    WebGPUCacheRenderPipeline.NumCacheHitWithHash = 0;
    WebGPUCacheRenderPipeline.NumCacheMiss = 0;
    WebGPUCacheRenderPipeline.NumPipelineCreationLastFrame = 0;
    WebGPUCacheRenderPipeline._NumPipelineCreationCurrentFrame = 0;
    return WebGPUCacheRenderPipeline;
}());
export { WebGPUCacheRenderPipeline };
//# sourceMappingURL=webgpuCacheRenderPipeline.js.map